import { createFileRoute, useNavigate } from "@tanstack/react-router";
import React, { Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Tab, TabMenu } from "src/components/base/tab-menu";
import TabLayout from "src/components/base/tab-layout";
import { BackButton } from "src/components/base/button";
import SINGLE_TAG_CONTEXT, { SingleTagProvider } from "src/context/internal/tag";
import { Badge } from "src/components/base/badge";
import {
    ArrowsRightLeftIcon,
    ChevronDownIcon,
    LockClosedIcon,
    PencilSquareIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import {
    Dropdown,
    DropdownButton,
    DropdownHeading,
    DropdownItem,
    DropdownLabel,
    DropdownMenu,
    DropdownSection,
} from "src/components/base/dropdown";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";
import RenameTagDialog from "src/components/dialogs/rename-tag";
import ChangeTagDescDialog from "src/components/dialogs/change-tag-desc";
import DeleteTagDialog from "src/components/dialogs/delete-tag";
import MoveTagFromDirectoryDialog from "src/components/dialogs/move-tag-from-directory";
import Outlet from "src/components/base/outlet";

/**
 * The properties for {@link SingleTagView}
 */
export type TagLayout = {};

/**
 * The Layout for a single tag
 */
function SingleTagView(props: TagLayout) {
    const [t] = useTranslation("admin-tag");
    const [tg] = useTranslation();

    const navigate = useNavigate();

    const { tagId } = Route.useParams();

    const [openRenameTag, setOpenRenameTag] = React.useState<SimpleTag | undefined>(undefined);
    const [openSetTagDesc, setOpenSetTagDesc] = React.useState<SimpleTag | undefined>(undefined);
    const [openDeleteTag, setOpenDeleteTag] = React.useState<SimpleTag | undefined>(undefined);
    const [openMoveFromDirectory, setOpenMoveFromDirectory] = React.useState<SimpleTag | undefined>(undefined);

    return (
        <SingleTagProvider uuid={tagId}>
            <SINGLE_TAG_CONTEXT.Consumer>
                {(ctx) => (
                    <div className={"flex flex-col gap-6"}>
                        <BackButton className={"self-start"} href={"/a/tags"}>
                            {t("button.back-to-tag-overview")}
                        </BackButton>

                        <TabLayout
                            heading={t("heading.tag", { tag: ctx.tag.name })}
                            headingChildren={
                                <Dropdown>
                                    <DropdownButton outline={true}>
                                        <span>{tg("button.edit")}</span>
                                        <ChevronDownIcon />
                                    </DropdownButton>
                                    <DropdownMenu anchor={"bottom end"}>
                                        <DropdownSection>
                                            <DropdownItem
                                                disabled={ctx.tag.locked}
                                                onClick={() => setOpenRenameTag({ ...ctx.tag })}
                                            >
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.rename-tag")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem
                                                disabled={ctx.tag.locked}
                                                onClick={() => setOpenSetTagDesc({ ...ctx.tag })}
                                            >
                                                <PencilSquareIcon />
                                                <DropdownLabel>{t("button.set-tag-description")}</DropdownLabel>
                                            </DropdownItem>
                                            <DropdownItem
                                                disabled={ctx.tag.locked}
                                                onClick={() => setOpenMoveFromDirectory({ ...ctx.tag })}
                                            >
                                                <ArrowsRightLeftIcon />
                                                {t("button.move-tag-from-directory")}
                                            </DropdownItem>
                                        </DropdownSection>
                                        <DropdownSection>
                                            <DropdownHeading>{t("heading.danger-zone")}</DropdownHeading>
                                            <DropdownItem
                                                disabled={ctx.tag.locked}
                                                onClick={() => setOpenDeleteTag({ ...ctx.tag })}
                                            >
                                                <TrashIcon />
                                                <DropdownLabel>{t("button.delete-tag")}</DropdownLabel>
                                            </DropdownItem>
                                        </DropdownSection>
                                    </DropdownMenu>
                                </Dropdown>
                            }
                            headingDescription={
                                ctx.tag.locked ? (
                                    <Badge color={"orange"}>
                                        <LockClosedIcon className={"size-4"} />
                                        <span>{t("label.read-only")}</span>
                                    </Badge>
                                ) : undefined
                            }
                            tabs={
                                <TabMenu>
                                    <Tab href={"/a/tags/$tagId/associated"} params={{ tagId }}>
                                        {t("label.associated-devices")}
                                    </Tab>
                                    <Tab href={"/a/tags/$tagId/inbound-acls"} params={{ tagId }}>
                                        {t("label.inbound-acls")}
                                    </Tab>
                                    <Tab href={"/a/tags/$tagId/outbound-acls"} params={{ tagId }}>
                                        {t("label.outbound-acls")}
                                    </Tab>
                                </TabMenu>
                            }
                        >
                            <Outlet />
                        </TabLayout>

                        <Suspense>
                            <RenameTagDialog
                                tag={openRenameTag}
                                isOpen={!!openRenameTag}
                                onClose={() => setOpenRenameTag(undefined)}
                                onSubmit={() => {
                                    setOpenRenameTag(undefined);
                                    ctx.reset();
                                }}
                            />

                            <ChangeTagDescDialog
                                tag={openSetTagDesc}
                                isOpen={!!openSetTagDesc}
                                onClose={() => setOpenSetTagDesc(undefined)}
                                onSubmit={() => setOpenSetTagDesc(undefined)}
                            />

                            <MoveTagFromDirectoryDialog
                                tag={openMoveFromDirectory}
                                isOpen={!!openMoveFromDirectory}
                                onSubmit={() => {
                                    setOpenMoveFromDirectory(undefined);
                                    ctx.reset();
                                }}
                                onClose={() => setOpenMoveFromDirectory(undefined)}
                            />

                            <DeleteTagDialog
                                tag={openDeleteTag}
                                isOpen={!!openDeleteTag}
                                onDeletion={async () => {
                                    setOpenDeleteTag(undefined);
                                    await navigate({ to: "/a/tags", search: { search: "" } });
                                }}
                                onClose={() => setOpenDeleteTag(undefined)}
                            />
                        </Suspense>
                    </div>
                )}
            </SINGLE_TAG_CONTEXT.Consumer>
        </SingleTagProvider>
    );
}

export const Route = createFileRoute("/_admin/a/tags/$tagId/_tags")({
    component: SingleTagView,
});
